import {
    Box,
    Button,
    Icon,
    Popover,
    PopoverArrow,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
    Spinner,
    Textarea,
    VStack
} from '@chakra-ui/react';
import { EventType } from '@components/Event/index';
import { useLivestreamData } from '@components/Event/provider/LivestreamDataProvider';
import { useFirebaseApp, useLivestreamMessage } from '@lib/firebase';
import { firebase } from '@lib/firebase';
import React, { ChangeEvent, useState } from 'react';
import {
    FaCheckCircle,
    FaCommentMedical,
    FaExclamationCircle,
    FaTelegramPlane
} from 'react-icons/fa';
import { useIntl } from 'react-intl';

interface IEventQuestionsProps {
    event: EventType;
}

enum QuestionState {
    DEFAULT = 'default',
    SUBMITTING = 'submitting',
    SUCCESS = 'success',
    FAILED = 'failed'
}

interface IRightElementChildProps {
    state: QuestionState;
}

const StateIcon: React.FunctionComponent<IRightElementChildProps> = ({ state }) => {
    switch (state) {
        case QuestionState.DEFAULT:
            return <Icon as={FaTelegramPlane} />;

        case QuestionState.SUBMITTING:
            return <Spinner size="1rem" />;

        case QuestionState.SUCCESS:
            return <Icon as={FaCheckCircle} color="green.500" />;

        case QuestionState.FAILED:
            return <Icon as={FaExclamationCircle} color="red.500" />;
    }
    return null;
};

const EventQuestions: React.FunctionComponent<IEventQuestionsProps> = ({ event }) => {
    const [value, setValue] = useState('');
    const [state, setState] = useState<QuestionState>(QuestionState.DEFAULT);
    const [isOpen, setIsOpen] = React.useState(false);
    const openPopover = () => setIsOpen(!isOpen);
    const closePopover = () => setIsOpen(false);
    const { app } = useFirebaseApp();
    const initialFocusRef = React.useRef<HTMLTextAreaElement>(null);
    const intl = useIntl();
    const { currentProgram } = useLivestreamData();
    const [message] = useLivestreamMessage(event.id);

    const submitQuestion = async () => {
        try {
            await app
                .firestore()
                .collection(`events/${event.id}/questions`)
                .add({
                    message: value,
                    user_id: app.auth().currentUser?.uid,
                    event_id: event.id,
                    event_schedule_id: currentProgram?.id || '',
                    created_at: firebase.firestore.FieldValue.serverTimestamp(),
                    user_agent: navigator?.userAgent || '',
                    device_vendor: navigator?.vendor || ''
                });
            setValue('');
            setTimeout(closePopover, 5000);
            setState(QuestionState.SUCCESS);
        } catch (err) {
            setState(QuestionState.FAILED);
        }
    };

    const onFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (state !== QuestionState.SUBMITTING) {
            setState(QuestionState.SUBMITTING);
            submitQuestion();
        }
    };

    const resetForm = () => {
        if (state === QuestionState.FAILED || state === QuestionState.SUCCESS) {
            setState(QuestionState.DEFAULT);
        }
    };

    const onChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        if (state !== QuestionState.SUBMITTING) {
            setValue(e.target.value);
            if (state === QuestionState.FAILED || state === QuestionState.SUCCESS) {
                setState(QuestionState.DEFAULT);
            }
        }
    };

    if (message && message.full_cover) {
        return null;
    }

    return (
        <Box>
            <Popover
                placement="auto-start"
                onOpen={resetForm}
                isOpen={isOpen}
                returnFocusOnClose={false}
                initialFocusRef={initialFocusRef}>
                <PopoverTrigger>
                    <Button
                        onClick={openPopover}
                        size="sm"
                        textTransform="uppercase"
                        leftIcon={<FaCommentMedical size="20px" />}
                        variant="solid"
                        colorScheme="brand">
                        {isOpen
                            ? intl.formatMessage({
                                  id: 'player.question.cta.back',
                                  defaultMessage: 'Zurück'
                              })
                            : intl.formatMessage({
                                  id: 'player.question.cta.default',
                                  defaultMessage: 'Fragen'
                              })}
                    </Button>
                </PopoverTrigger>
                <PopoverContent>
                    <PopoverArrow />
                    <PopoverBody>
                        <form onSubmit={onFormSubmit}>
                            <VStack maxWidth="300px" spacing="2">
                                <Textarea
                                    disabled={state === QuestionState.SUBMITTING}
                                    ref={initialFocusRef}
                                    resize="none"
                                    rows={3}
                                    p="1"
                                    placeholder={intl.formatMessage({
                                        id: 'player.question.form.field_message.placeholder',
                                        defaultMessage: 'Fragen eingeben ...'
                                    })}
                                    autoComplete="off"
                                    width="100%"
                                    zIndex={5}
                                    onChange={onChange}
                                    value={value}
                                />
                                <Button
                                    type="submit"
                                    isLoading={state === QuestionState.SUBMITTING}
                                    disabled={
                                        state === QuestionState.SUBMITTING || value.length < 10
                                    }
                                    variant="ghost"
                                    leftIcon={<StateIcon state={state} />}>
                                    {QuestionState.SUCCESS === state
                                        ? intl.formatMessage({
                                              id:
                                                  'player.question.form.submit_button.submitted_success',
                                              defaultMessage: 'Gesendet'
                                          })
                                        : intl.formatMessage({
                                              id: 'player.question.form.submit_button.default',
                                              defaultMessage: 'Senden'
                                          })}
                                </Button>
                            </VStack>
                        </form>
                    </PopoverBody>
                </PopoverContent>
            </Popover>
        </Box>
    );
};

export default EventQuestions;
